/**
 * Security Assistant Context
 *
 * Provides application-wide AI security assistance with context-aware
 * suggestions, attack analysis, and security recommendations.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import type {
  AssistantContext,
  AssistantState,
  AssistantSuggestion,
  AssistantConfig,
  ComponentContext,
  RiskContext,
  MitigationContext,
  DiagramContext,
  SpaceContext,
  AttackScenarioAnalysis,
  ComplianceGap,
  AssistantTab,
  Suggestion,
  CreateRiskDto,
  CreateMitigationDto,
  Risk,
  Mitigation,
  CIADimension,
} from 'shared';
import { SuggestionPriority, RiskTreatment, ControlType } from 'shared';
import {
  aiApi,
  riskApi,
  mitigationApi,
  type AttackScenario,
} from '../services/risk.service';
import { RiskSelectorDialog } from '../components/SecurityAssistant/RiskSelectorDialog';

// =============================================================================
// CONSTANTS
// =============================================================================

const DEFAULT_CONFIG: AssistantConfig = {
  autoAnalyze: false, // User must explicitly request AI analysis
  showOnContextChange: false,
  maxSuggestions: 10,
  enableAttackSimulation: true,
  enableComplianceCheck: true,
};

const ANALYSIS_DEBOUNCE_MS = 1000;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// =============================================================================
// CONTEXT INTERFACE
// =============================================================================

interface SecurityAssistantContextValue {
  // State
  state: AssistantState;
  config: AssistantConfig;
  activeTab: AssistantTab;

  // Panel controls
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  setActiveTab: (tab: AssistantTab) => void;

  // Context management
  setComponentContext: (
    data: Omit<ComponentContext, 'type' | 'timestamp'>
  ) => void;
  setRiskContext: (data: Omit<RiskContext, 'type' | 'timestamp'>) => void;
  setMitigationContext: (
    data: Omit<MitigationContext, 'type' | 'timestamp'>
  ) => void;
  setDiagramContext: (data: Omit<DiagramContext, 'type' | 'timestamp'>) => void;
  setSpaceContext: (data: Omit<SpaceContext, 'type' | 'timestamp'>) => void;
  clearContext: () => void;

  // Analysis actions
  analyzeContext: () => Promise<void>;
  runAttackSimulation: () => Promise<void>;
  checkCompliance: () => Promise<void>;
  refreshSuggestions: () => Promise<void>;

  // Suggestion actions
  acceptSuggestion: (suggestion: AssistantSuggestion) => Promise<void>;
  dismissSuggestion: (suggestionId: string) => void;
  deferSuggestion: (suggestionId: string) => void;

  // Risk selector dialog (for mitigations without risk context)
  riskSelectorOpen: boolean;
  availableRisks: Risk[];
  pendingMitigationSuggestion: AssistantSuggestion | null;
  handleRiskSelected: (riskId: string) => Promise<void>;
  handleRiskSelectorClose: () => void;

  // Configuration
  updateConfig: (updates: Partial<AssistantConfig>) => void;

  // Computed values
  pendingSuggestionCount: number;
  hasHighPrioritySuggestions: boolean;
}

// =============================================================================
// CONTEXT CREATION
// =============================================================================

const SecurityAssistantContext =
  createContext<SecurityAssistantContextValue | null>(null);

// =============================================================================
// CACHE MANAGEMENT
// =============================================================================

interface CacheEntry {
  contextKey: string;
  suggestions: AssistantSuggestion[];
  attackScenarios: AttackScenarioAnalysis[];
  complianceGaps: ComplianceGap[];
  timestamp: number;
}

const analysisCache = new Map<string, CacheEntry>();

function getContextKey(context: AssistantContext): string {
  if (context.type === 'none') return 'none';
  return `${context.type}:${context.spaceId}:${(context as unknown as { [key: string]: string })[`${context.type}Id`] || 'space'}`;
}

/**
 * Extract componentId from various context types
 */
function getComponentIdFromContext(context: AssistantContext): string | null {
  switch (context.type) {
    case 'component':
      return context.componentId;
    case 'risk':
      return context.componentId || null;
    case 'mitigation':
      // Mitigations don't directly have componentId, would need to look up via risk
      return null;
    case 'diagram':
      // Return first componentId for diagram context
      return context.componentIds?.[0] || null;
    case 'space':
    case 'none':
    default:
      return null;
  }
}

function getCachedAnalysis(context: AssistantContext): CacheEntry | null {
  const key = getContextKey(context);
  const entry = analysisCache.get(key);

  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    analysisCache.delete(key);
    return null;
  }

  return entry;
}

function setCachedAnalysis(
  context: AssistantContext,
  suggestions: AssistantSuggestion[],
  attackScenarios: AttackScenarioAnalysis[],
  complianceGaps: ComplianceGap[]
): void {
  const key = getContextKey(context);
  analysisCache.set(key, {
    contextKey: key,
    suggestions,
    attackScenarios,
    complianceGaps,
    timestamp: Date.now(),
  });
}

// =============================================================================
// PROVIDER COMPONENT
// =============================================================================

interface SecurityAssistantProviderProps {
  children: ReactNode;
}

export const SecurityAssistantProvider: React.FC<
  SecurityAssistantProviderProps
> = ({ children }) => {
  // State
  const [state, setState] = useState<AssistantState>({
    isOpen: false,
    isLoading: false,
    isAnalyzing: false,
    currentContext: { type: 'none', spaceId: '', timestamp: new Date() },
    suggestions: [],
    attackScenarios: [],
    complianceGaps: [],
  });

  const [config, setConfig] = useState<AssistantConfig>(DEFAULT_CONFIG);
  const [activeTab, setActiveTab] = useState<AssistantTab>('insights'); // Default to insights

  // Risk selector dialog state for mitigations without a risk context
  const [riskSelectorOpen, setRiskSelectorOpen] = useState(false);
  const [pendingMitigationSuggestion, setPendingMitigationSuggestion] =
    useState<AssistantSuggestion | null>(null);
  const [availableRisks, setAvailableRisks] = useState<Risk[]>([]);

  // Refs for debouncing
  const analyzeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastContextRef = useRef<string>('');

  // ==========================================================================
  // PANEL CONTROLS
  // ==========================================================================

  const openPanel = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: true }));
  }, []);

  const closePanel = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: false }));
  }, []);

  const togglePanel = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: !prev.isOpen }));
  }, []);

  // ==========================================================================
  // CONTEXT MANAGEMENT
  // ==========================================================================

  const updateContext = useCallback((newContext: AssistantContext) => {
    const contextKey = getContextKey(newContext);

    // Don't update if context hasn't changed
    if (contextKey === lastContextRef.current) return;
    lastContextRef.current = contextKey;

    // Check cache first
    const cached = getCachedAnalysis(newContext);
    if (cached) {
      setState(prev => ({
        ...prev,
        currentContext: newContext,
        suggestions: cached.suggestions,
        attackScenarios: cached.attackScenarios,
        complianceGaps: cached.complianceGaps,
        lastAnalyzedAt: new Date(cached.timestamp),
      }));
      return;
    }

    // Update context without suggestions
    setState(prev => ({
      ...prev,
      currentContext: newContext,
      suggestions: [],
      attackScenarios: [],
      complianceGaps: [],
    }));
  }, []);

  const setComponentContext = useCallback(
    (data: Omit<ComponentContext, 'type' | 'timestamp'>) => {
      updateContext({
        type: 'component',
        timestamp: new Date(),
        ...data,
      });
    },
    [updateContext]
  );

  const setRiskContext = useCallback(
    (data: Omit<RiskContext, 'type' | 'timestamp'>) => {
      updateContext({
        type: 'risk',
        timestamp: new Date(),
        ...data,
      });
    },
    [updateContext]
  );

  const setMitigationContext = useCallback(
    (data: Omit<MitigationContext, 'type' | 'timestamp'>) => {
      updateContext({
        type: 'mitigation',
        timestamp: new Date(),
        ...data,
      });
    },
    [updateContext]
  );

  const setDiagramContext = useCallback(
    (data: Omit<DiagramContext, 'type' | 'timestamp'>) => {
      updateContext({
        type: 'diagram',
        timestamp: new Date(),
        ...data,
      });
    },
    [updateContext]
  );

  const setSpaceContext = useCallback(
    (data: Omit<SpaceContext, 'type' | 'timestamp'>) => {
      updateContext({
        type: 'space',
        timestamp: new Date(),
        ...data,
      });
    },
    [updateContext]
  );

  const clearContext = useCallback(() => {
    lastContextRef.current = '';
    setState(prev => ({
      ...prev,
      currentContext: { type: 'none', spaceId: '', timestamp: new Date() },
      suggestions: [],
      attackScenarios: [],
      complianceGaps: [],
    }));
  }, []);

  // ==========================================================================
  // ANALYSIS ACTIONS
  // ==========================================================================

  const analyzeContext = useCallback(async () => {
    const { currentContext } = state;
    if (currentContext.type === 'none') return;

    setState(prev => ({ ...prev, isAnalyzing: true, error: undefined }));

    try {
      let result;

      // Handle risk context differently - suggest mitigations for the specific risk
      if (currentContext.type === 'risk') {
        console.log(
          '[SecurityAssistant] Analyzing risk context:',
          currentContext.riskId
        );
        result = await aiApi.riskAnalysis(
          currentContext.riskId,
          currentContext.spaceId
        );
      } else {
        // Extract componentId from context for component-based analysis
        const componentId = getComponentIdFromContext(currentContext);
        if (!componentId) {
          console.log(
            '[SecurityAssistant] No componentId available for analysis'
          );
          setState(prev => ({ ...prev, isAnalyzing: false }));
          return;
        }

        // Call the component analysis API
        result = await aiApi.securityAnalysis(
          componentId,
          currentContext.spaceId,
          true
        );
      }

      // Transform API response to AssistantSuggestion format
      const suggestions: AssistantSuggestion[] = result.suggestions.map(
        (s: Suggestion, index: number) => ({
          id: s.id || `suggestion-${Date.now()}-${index}`,
          type: s.type as AssistantSuggestion['type'],
          priority: s.priority,
          confidence: s.confidence,
          contextType: currentContext.type,
          contextId: getContextId(currentContext),
          title: s.title,
          description: s.description,
          reasoning: s.reasoning,
          actionType: mapSuggestionTypeToAction(s.type),
          actionData: s.riskTemplate || s.mitigationTemplate,
          attackTechniqueId: s.attackTechniqueId,
          attackTechniqueUrl: s.attackTechniqueUrl,
          d3fendId: s.d3fendId,
          d3fendUrl: s.d3fendUrl,
          isoControlId: s.isoControlId,
          isoControlUrl: s.isoControlUrl,
          source: s.source,
          status: 'pending',
          spaceId: currentContext.spaceId,
          targetRiskId:
            currentContext.type === 'risk' ? currentContext.riskId : undefined,
          createdAt: new Date(),
        })
      );

      // Cache the results
      setCachedAnalysis(currentContext, suggestions, [], []);

      setState(prev => ({
        ...prev,
        isAnalyzing: false,
        suggestions,
        lastAnalyzedAt: new Date(),
      }));
    } catch (error) {
      console.error('[SecurityAssistant] Analysis failed:', error);
      setState(prev => ({
        ...prev,
        isAnalyzing: false,
        error: error instanceof Error ? error.message : 'Analysis failed',
      }));
    }
  }, [state]);

  const runAttackSimulation = useCallback(async () => {
    const { currentContext } = state;
    if (currentContext.type === 'none') return;

    setState(prev => ({ ...prev, isAnalyzing: true }));

    try {
      // Extract componentId from context for API call
      const componentId = getComponentIdFromContext(currentContext);
      if (!componentId) {
        console.log(
          '[SecurityAssistant] No componentId available for attack simulation'
        );
        setState(prev => ({ ...prev, isAnalyzing: false }));
        return;
      }

      const result = await aiApi.attackSimulation(
        componentId,
        currentContext.spaceId,
        true,
        5
      );

      // Transform API response to AttackScenarioAnalysis format
      const impactToNumber = (impact: string): number => {
        const mapping: Record<string, number> = {
          critical: 10,
          high: 7.5,
          medium: 5,
          low: 2.5,
        };
        return mapping[impact] ?? 5;
      };

      const attackScenarios: AttackScenarioAnalysis[] = (
        result.attackScenarios || []
      ).map((scenario: AttackScenario, index: number) => ({
        id: scenario.id || `attack-${Date.now()}-${index}`,
        name: scenario.name,
        description: scenario.description,
        threatActor: 'Unknown',
        likelihood: scenario.likelihood,
        impact: impactToNumber(scenario.impact),
        riskScore: scenario.likelihood * 2, // Derive from likelihood
        attackPath: scenario.attackPath.map(
          (step: AttackScenario['attackPath'][number], stepIndex: number) => ({
            stepNumber: step.step || stepIndex + 1,
            technique: step.technique,
            techniqueId: step.techniqueId,
            target: step.targetComponent || '',
            targetId: undefined,
            description: step.description,
            prerequisites: [] as string[],
            impact: 'medium' as const,
          })
        ),
        affectedComponents: [] as string[],
        suggestedMitigations: scenario.mitigations || [],
        contextType: currentContext.type,
        contextId: getContextId(currentContext),
        createdAt: new Date(),
      }));

      setState(prev => ({
        ...prev,
        isAnalyzing: false,
        attackScenarios,
      }));
    } catch (error) {
      console.error('[SecurityAssistant] Attack simulation failed:', error);
      setState(prev => ({
        ...prev,
        isAnalyzing: false,
        error:
          error instanceof Error ? error.message : 'Attack simulation failed',
      }));
    }
  }, [state]);

  const checkCompliance = useCallback(async () => {
    // TODO: Implement compliance checking against frameworks
    setState(prev => ({ ...prev, complianceGaps: [] }));
  }, []);

  const refreshSuggestions = useCallback(async () => {
    // Clear cache for current context and re-analyze
    const { currentContext } = state;
    if (currentContext.type !== 'none') {
      const key = getContextKey(currentContext);
      analysisCache.delete(key);
    }
    await analyzeContext();
  }, [state, analyzeContext]);

  // ==========================================================================
  // SUGGESTION ACTIONS
  // ==========================================================================

  const acceptSuggestion = useCallback(
    async (suggestion: AssistantSuggestion) => {
      // Mark as processing
      setState(prev => ({
        ...prev,
        suggestions: prev.suggestions.map(s =>
          s.id === suggestion.id ? { ...s, status: 'accepted' as const } : s
        ),
      }));

      try {
        // Execute the action based on actionType
        if (suggestion.actionType === 'create_risk' && suggestion.actionData) {
          // Map the risk template to CreateRiskDto
          const template = suggestion.actionData as Partial<Risk>;
          const componentId = suggestion.contextId;

          if (!componentId) {
            throw new Error(
              'Cannot create risk: No component ID in suggestion context'
            );
          }

          const riskDto: CreateRiskDto = {
            title: template.title || suggestion.title,
            description: template.description || suggestion.description,
            threatSource:
              template.threatSource || 'AI-identified threat source',
            vulnerability:
              template.vulnerability || 'AI-identified vulnerability',
            existingControls: template.existingControls || [],
            attackTechniqueId:
              template.attackTechniqueId || suggestion.attackTechniqueId,
            confidentialityImpact: template.confidentialityImpact || 5,
            integrityImpact: template.integrityImpact || 5,
            availabilityImpact: template.availabilityImpact || 5,
            likelihood: template.likelihood || 5,
            riskTreatment: template.riskTreatment || RiskTreatment.MITIGATE,
            owner: 'Unassigned', // Will be set by user
            componentId,
            spaceId: suggestion.spaceId,
          };

          const createdRisk = await riskApi.create(riskDto);
          console.info(
            '[SecurityAssistant] Risk created:',
            suggestion.title,
            '- ID:',
            createdRisk.id
          );
        } else if (
          suggestion.actionType === 'create_mitigation' &&
          suggestion.actionData
        ) {
          // Map the mitigation template to CreateMitigationDto
          const template = suggestion.actionData as Partial<Mitigation>;

          // For mitigations, we need a riskId from:
          // 1. Risk context (if navigated to a risk)
          // 2. Template (if AI linked it)
          const riskId =
            suggestion.contextType === 'risk' && suggestion.contextId
              ? suggestion.contextId
              : template.riskId;

          if (!riskId) {
            // No risk context - show risk selector dialog
            // First, fetch available risks for the component
            const componentId =
              suggestion.contextId ||
              getComponentIdFromContext(state.currentContext);
            if (componentId) {
              try {
                const risks = await riskApi.getByComponent(componentId);
                if (risks.length === 0) {
                  throw new Error(
                    'Cannot create mitigation: No risks exist for this component. Create a risk first.'
                  );
                }
                setAvailableRisks(risks);
                setPendingMitigationSuggestion(suggestion);
                setRiskSelectorOpen(true);
                // Reset suggestion status - will be processed after risk selection
                setState(prev => ({
                  ...prev,
                  suggestions: prev.suggestions.map(s =>
                    s.id === suggestion.id
                      ? { ...s, status: 'pending' as const }
                      : s
                  ),
                }));
                return; // Exit early - will continue in handleRiskSelected
              } catch {
                throw new Error(
                  'Cannot create mitigation: No risk ID in context. Navigate to a risk first.'
                );
              }
            } else {
              throw new Error(
                'Cannot create mitigation: No risk ID in context. Navigate to a risk first.'
              );
            }
          }

          const mitigationDto: CreateMitigationDto = {
            title: template.title || suggestion.title,
            description: template.description || suggestion.description,
            riskId,
            controlType: template.controlType || ControlType.PREVENTIVE,
            isoControlId: template.isoControlId || suggestion.isoControlId,
            isoControlName: template.isoControlName,
            d3fendId: template.d3fendId || suggestion.d3fendId,
            reductionFactor: template.reductionFactor || 0.5,
            appliesTo:
              template.appliesTo ||
              ([
                'confidentiality',
                'integrity',
                'availability',
              ] as CIADimension[]),
            priority: template.priority || suggestion.priority,
            owner: 'Unassigned', // Will be set by user
          };

          await mitigationApi.create(mitigationDto);
          console.info(
            '[SecurityAssistant] Mitigation created:',
            suggestion.title
          );
        } else {
          // For review/info types, just mark as accepted
          console.info(
            '[SecurityAssistant] Suggestion accepted (no action):',
            suggestion.title
          );
        }

        // Remove from list after brief delay for visual feedback
        setTimeout(() => {
          setState(prev => ({
            ...prev,
            suggestions: prev.suggestions.filter(s => s.id !== suggestion.id),
          }));
        }, 300);
      } catch (error) {
        console.error(
          '[SecurityAssistant] Failed to execute suggestion action:',
          error
        );
        // Revert status on error
        setState(prev => ({
          ...prev,
          suggestions: prev.suggestions.map(s =>
            s.id === suggestion.id ? { ...s, status: 'pending' as const } : s
          ),
        }));
        throw error; // Re-throw so caller can handle it
      }
    },
    []
  );

  const dismissSuggestion = useCallback((suggestionId: string) => {
    setState(prev => ({
      ...prev,
      suggestions: prev.suggestions.map(s =>
        s.id === suggestionId ? { ...s, status: 'dismissed' as const } : s
      ),
    }));

    // Remove from list after brief delay
    setTimeout(() => {
      setState(prev => ({
        ...prev,
        suggestions: prev.suggestions.filter(s => s.id !== suggestionId),
      }));
    }, 300);
  }, []);

  const deferSuggestion = useCallback((suggestionId: string) => {
    setState(prev => ({
      ...prev,
      suggestions: prev.suggestions.map(s =>
        s.id === suggestionId ? { ...s, status: 'deferred' as const } : s
      ),
    }));
  }, []);

  // Handler for when user selects a risk from the selector dialog
  const handleRiskSelected = useCallback(
    async (riskId: string) => {
      if (!pendingMitigationSuggestion) return;

      const suggestion = pendingMitigationSuggestion;
      const template = suggestion.actionData as Partial<Mitigation>;

      // Mark as processing
      setState(prev => ({
        ...prev,
        suggestions: prev.suggestions.map(s =>
          s.id === suggestion.id ? { ...s, status: 'accepted' as const } : s
        ),
      }));

      try {
        const mitigationDto: CreateMitigationDto = {
          title: template.title || suggestion.title,
          description: template.description || suggestion.description,
          riskId,
          controlType: template.controlType || ControlType.PREVENTIVE,
          isoControlId: template.isoControlId || suggestion.isoControlId,
          isoControlName: template.isoControlName,
          d3fendId: template.d3fendId || suggestion.d3fendId,
          reductionFactor: template.reductionFactor || 0.5,
          appliesTo:
            template.appliesTo ||
            ([
              'confidentiality',
              'integrity',
              'availability',
            ] as CIADimension[]),
          priority: template.priority || suggestion.priority,
          owner: 'Unassigned',
        };

        await mitigationApi.create(mitigationDto);
        console.info(
          '[SecurityAssistant] Mitigation created:',
          suggestion.title
        );

        // Remove from list
        setTimeout(() => {
          setState(prev => ({
            ...prev,
            suggestions: prev.suggestions.filter(s => s.id !== suggestion.id),
          }));
        }, 300);
      } catch (error) {
        console.error(
          '[SecurityAssistant] Failed to create mitigation:',
          error
        );
        // Revert status on error
        setState(prev => ({
          ...prev,
          suggestions: prev.suggestions.map(s =>
            s.id === suggestion.id ? { ...s, status: 'pending' as const } : s
          ),
        }));
      } finally {
        // Close dialog and clear pending suggestion
        setRiskSelectorOpen(false);
        setPendingMitigationSuggestion(null);
        setAvailableRisks([]);
      }
    },
    [pendingMitigationSuggestion]
  );

  const handleRiskSelectorClose = useCallback(() => {
    setRiskSelectorOpen(false);
    setPendingMitigationSuggestion(null);
    setAvailableRisks([]);
  }, []);

  // ==========================================================================
  // CONFIGURATION
  // ==========================================================================

  const updateConfig = useCallback((updates: Partial<AssistantConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  }, []);

  // ==========================================================================
  // AUTO-ANALYZE ON CONTEXT CHANGE
  // ==========================================================================

  // Use a ref to track the last analyzed context to prevent loops
  const lastAnalyzedContextRef = useRef<string>('');

  useEffect(() => {
    if (!config.autoAnalyze) return;
    if (state.currentContext.type === 'none') return;
    if (state.isAnalyzing) return; // Don't trigger if already analyzing

    // Create a key for the current context
    const contextKey = getContextKey(state.currentContext);

    // Skip if we've already analyzed this exact context
    if (contextKey === lastAnalyzedContextRef.current) return;

    // Debounce analysis
    if (analyzeTimeoutRef.current) {
      clearTimeout(analyzeTimeoutRef.current);
    }

    analyzeTimeoutRef.current = setTimeout(() => {
      lastAnalyzedContextRef.current = contextKey;
      analyzeContext();
    }, ANALYSIS_DEBOUNCE_MS);

    return () => {
      if (analyzeTimeoutRef.current) {
        clearTimeout(analyzeTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentContext.type, config.autoAnalyze]);

  // ==========================================================================
  // COMPUTED VALUES
  // ==========================================================================

  const pendingSuggestionCount = state.suggestions.filter(
    s => s.status === 'pending'
  ).length;

  const hasHighPrioritySuggestions = state.suggestions.some(
    s => s.status === 'pending' && s.priority === SuggestionPriority.HIGH
  );

  // ==========================================================================
  // CONTEXT VALUE
  // ==========================================================================

  const value: SecurityAssistantContextValue = {
    state,
    config,
    activeTab,
    openPanel,
    closePanel,
    togglePanel,
    setActiveTab,
    setComponentContext,
    setRiskContext,
    setMitigationContext,
    setDiagramContext,
    setSpaceContext,
    clearContext,
    analyzeContext,
    runAttackSimulation,
    checkCompliance,
    refreshSuggestions,
    acceptSuggestion,
    dismissSuggestion,
    deferSuggestion,
    riskSelectorOpen,
    availableRisks,
    pendingMitigationSuggestion,
    handleRiskSelected,
    handleRiskSelectorClose,
    updateConfig,
    pendingSuggestionCount,
    hasHighPrioritySuggestions,
  };

  return (
    <SecurityAssistantContext.Provider value={value}>
      {children}
      <RiskSelectorDialog
        open={riskSelectorOpen}
        onClose={handleRiskSelectorClose}
        onSelect={handleRiskSelected}
        risks={availableRisks}
        mitigationSuggestion={pendingMitigationSuggestion}
      />
    </SecurityAssistantContext.Provider>
  );
};

// =============================================================================
// HOOKS
// =============================================================================

export function useSecurityAssistant(): SecurityAssistantContextValue {
  const context = useContext(SecurityAssistantContext);
  if (!context) {
    throw new Error(
      'useSecurityAssistant must be used within a SecurityAssistantProvider'
    );
  }
  return context;
}

/**
 * Optional version of useSecurityAssistant that returns null when outside the provider.
 * Useful for components that may be rendered in contexts without the SecurityAssistantProvider
 * (e.g., public embed pages).
 */
export function useSecurityAssistantOptional(): SecurityAssistantContextValue | null {
  return useContext(SecurityAssistantContext);
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/*
 * Build analysis context for future use with more generic AI endpoints
 * @deprecated Currently not used - aiApi uses componentId/spaceId directly
 *
function _buildAnalysisContext(
  context: AssistantContext
): Record<string, unknown> {
  switch (context.type) {
    case 'component':
      return {
        type: 'component',
        componentId: context.componentId,
        componentName: context.componentName,
        componentType: context.componentType,
        confidentialityScore: context.confidentialityScore,
        integrityScore: context.integrityScore,
        availabilityScore: context.availabilityScore,
        dataCategories: context.dataCategories,
        environment: context.environment,
        spaceId: context.spaceId,
      };
    case 'risk':
      return {
        type: 'risk',
        riskId: context.riskId,
        riskTitle: context.riskTitle,
        componentId: context.componentId,
        threatSource: context.threatSource,
        vulnerability: context.vulnerability,
        inherentRisk: context.inherentRisk,
        residualRisk: context.residualRisk,
        spaceId: context.spaceId,
      };
    case 'mitigation':
      return {
        type: 'mitigation',
        mitigationId: context.mitigationId,
        riskId: context.riskId,
        controlType: context.controlType,
        implementationStatus: context.implementationStatus,
        spaceId: context.spaceId,
      };
    case 'diagram':
      return {
        type: 'diagram',
        diagramId: context.diagramId,
        componentIds: context.componentIds,
        spaceId: context.spaceId,
      };
    case 'space':
      return {
        type: 'space',
        spaceId: context.spaceId,
        componentCount: context.componentCount,
        riskCount: context.riskCount,
      };
    default:
      return { type: 'none' };
  }
}
*/

function getContextId(context: AssistantContext): string {
  switch (context.type) {
    case 'component':
      return context.componentId;
    case 'risk':
      return context.riskId;
    case 'mitigation':
      return context.mitigationId;
    case 'diagram':
      return context.diagramId;
    case 'space':
      return context.spaceId;
    default:
      return '';
  }
}

function mapSuggestionTypeToAction(
  type: string
): AssistantSuggestion['actionType'] {
  switch (type) {
    case 'risk':
      return 'create_risk';
    case 'mitigation':
      return 'create_mitigation';
    case 'component':
    case 'relation':
      return 'add_relation';
    default:
      return 'review';
  }
}
